'use client';
import {useEffect, useState, useRef} from 'react';
import {Typography, TypographyProps} from '@mui/material';
import styles from './style.module.scss';

interface AnimatedTextProps extends TypographyProps {
  children: React.ReactNode;
  className?: string;
  animationType?:
    | 'zoomIn'
    | 'zoomOut'
    | 'slideInLeft'
    | 'slideInRight'
    | 'slideInTop'
    | 'slideInBottom';
  animationDuration?: number;
  animationDelay?: number;
}

const ParallaxText: React.FC<AnimatedTextProps> = ({
  children,
  variant = 'body1',
  className = '',
  animationType = 'zoomIn',
  animationDuration = 1000,
  animationDelay = 200,
  ...restProps // Allow passing other TypographyProps
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const elementRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!elementRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      {root: null, threshold: 0.3}
    );

    observer.observe(elementRef.current);

    return () => observer.disconnect();
  }, []);

  return (
    <Typography
      sx={{fontWeight: 'inherit'}}
      ref={elementRef}
      variant={variant as TypographyProps['variant']} // Ensure correct type
      className={`${styles.animatedText} ${
        isVisible ? `${styles.visible} ${styles[animationType]}` : ''
      } ${className}`}
      style={{
        transition: `opacity ${animationDuration}ms ease-out ${animationDelay}ms,
        transform ${animationDuration}ms ease-out ${animationDelay}ms`,
      }}
      {...restProps} // Spread extra Typography props
    >
      {children}
    </Typography>
  );
};

export default ParallaxText;
